// Modules
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { featureConfig } from 'config/features';
import { AnimatePresence, motion } from 'framer-motion';
import { usePermissions } from 'hooks/usePermissions';

// UI
import UserMenu from './UserMenu';
import LanguageSelector from 'components/common/LanguageSelector';
import {
  Sidebar,
  SidebarBody,
  SidebarDivider,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from 'components/catalyst/sidebar'
import {
  LayoutDashboard,
  FolderOpen,
  Building2,
  Wrench,
  Users,
  MapPin,
  Calendar,
  ClipboardList,
  Calculator,
  Bell,
  Moon,
  Sun,
  ChevronRight,
  Package,
  Receipt,
  ShieldCheck,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  FileText,
} from 'lucide-react';
import { Badge } from 'components/catalyst/badge';
import { Strong, Text } from 'components/catalyst/text';

// Utils
import { getAppVersion } from 'utils/version';
import { useAppSelector } from 'app/store';
import { selectAdminUser } from 'features/auth/authSlice';
import clsx from 'clsx';
import { isDynamicRouteMatch, submenuItemVariants, submenuVariants } from './utils';
import { getSidebarItemPermission, isSidebarItemAdminOnly } from 'utils/permissions';
import { Permission } from 'features/roles';

interface SidebarMainProps {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  showNotificationsModal: boolean;
  handleOpenNotificationsModal: () => void;
  isMinimized: boolean;
  toggleMinimized: () => void;
  unreadCount: number;
  userMenuExtraItems?: Array<{
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    path?: string;
    onClick?: () => void;
  }>;
}

interface NavigationItem {
  /** Unique identifier for the navigation item. Used to lookup permissions from config. */
  id?: string;
  name: string;
  href: string;
  icon: JSX.Element;
  enabled?: boolean;
  /** Permissions are derived from ROUTE_PERMISSIONS via the sidebar item id */
  matchDynamicRoute?: boolean;
  excludedSubroutes?: string[];
  children?: {
    name: string;
    href: string;
    icon: JSX.Element;
  }[];
}

const SidebarMain: FC<SidebarMainProps> = ({
  isDarkMode,
  toggleDarkMode,
  showNotificationsModal,
  handleOpenNotificationsModal,
  isMinimized,
  toggleMinimized,
  unreadCount,
  userMenuExtraItems,
}) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const { can, isSuperAdmin, isAdmin } = usePermissions();

  const user = useAppSelector(selectAdminUser);

  /**
   * Check if a navigation item should be accessible based on permissions.
   * Uses the centralized permission configuration and the can() helper.
   */
  const hasNavPermission = (itemId?: string, requiredPermission?: Permission | Permission[]): boolean => {
    // Get permission from config if itemId is provided and no requiredPermission specified
    const permission = requiredPermission ?? (itemId ? getSidebarItemPermission(itemId) : undefined);

    // No permission required - always accessible
    if (!permission) return true;

    // Use the can() helper which handles superAdmin/admin bypass
    return can(permission);
  };

  /**
   * Check if item is admin-only using centralized configuration.
   */
  const isItemAdminOnly = (itemId?: string, adminOnlyProp?: boolean): boolean => {
    // Explicit prop takes precedence
    if (adminOnlyProp !== undefined) return adminOnlyProp;
    // Check config
    return itemId ? isSidebarItemAdminOnly(itemId) : false;
  };

  const [panelState, setPanelState] = useState<Record<string, boolean>>({
    'work-orders': false,
    projects: false,
    leads: false,
  });

  const togglePanelState = (id: string, primary?: boolean) => {
    setPanelState((prevState) => ({
      ...prevState,
      [id]: primary ? true : !prevState[id],
    }));
  };

  // Navigation items get permissions derived from ROUTE_PERMISSIONS via SIDEBAR_ROUTE_MAP
  const primaryNavigationList: NavigationItem[] = useMemo(() => [
    {
      id: "dashboard",
      name: t('SIDEBAR_DASHBOARD'),
      href: "/dashboard",
      icon: <LayoutDashboard size={12} />,
      enabled: featureConfig.dashboard,
      // Permission loaded from config via getSidebarItemPermission('dashboard')
    },
    {
      id: "tasks",
      name: t('SIDEBAR_TASKS'),
      href: "/tasks",
      icon: <ClipboardList size={16} />,
      enabled: featureConfig.tasks,
    },
    {
      id: "projects",
      name: t('SIDEBAR_PROJECTS'),
      href: "/projects",
      icon: <FolderOpen size={16} />,
      enabled: featureConfig.projects,
      matchDynamicRoute: true,
      excludedSubroutes: ["/new"],
    },
    {
      id: "work-orders",
      name: t('SIDEBAR_WORK_ORDERS'),
      href: "/work-orders",
      icon: <Wrench size={16} />,
      enabled: featureConfig.workOrders,
      matchDynamicRoute: true,
      excludedSubroutes: ["/global-schedule", "/projects/templates"],
    },
    {
      id: 'leads',
      name: t('SIDEBAR_LEADS'),
      href: "/leads",
      icon: <Users size={16} />,
      enabled: featureConfig.leads,
    },
    {
      id: 'location',
      name: t('SIDEBAR_LOCATIONS'),
      href: "/location",
      icon: <MapPin size={16} />,
      enabled: featureConfig.leads,
    },
    {
      id: 'schedule',
      name: t('SIDEBAR_SCHEDULE'),
      href: "/schedule",
      icon: <Calendar size={16} />,
      enabled: featureConfig.schedule,
    },
    {
      id: 'inventory',
      name: t('SIDEBAR_INVENTORY'),
      href: "/inventory",
      icon: <Package size={16} />,
      enabled: featureConfig.inventory,
    },
    {
      id: 'accounting',
      name: t('SIDEBAR_ACCOUNTING'),
      href: "/accounting",
      icon: <Receipt size={16} />,
      enabled: featureConfig.accounting,
    },
  ], [t]);
  

  /**
   * Check if user can access admin-only items.
   * Only SUPERADMIN or ADMIN can access.
   */
  const canAccessAdminOnly = isSuperAdmin || isAdmin;

  // Secondary navigation items also get permissions from config
  const secondaryNavigationList: NavigationItem[] = [
    {
      id: 'company',
      name: t('SIDEBAR_COMPANY'),
      href: "/company",
      icon: <Building2 size={16} />,
      enabled: featureConfig.team || featureConfig.documents || featureConfig.leads,
      // adminOnly loaded from config via isSidebarItemAdminOnly('company')
    },
    {
      id: 'budget-templates',
      name: "Budget Templates",
      href: "/budget-templates",
      icon: <Calculator size={16} />,
      enabled: featureConfig.budgets,
      // Permission loaded from config via getSidebarItemPermission('budget-templates')
    },
    {
      id: 'quote-templates',
      name: "Quote Templates",
      href: "/quote-templates",
      icon: <FileText size={16} />,
      enabled: featureConfig.leads,
      // Permission loaded from config via getSidebarItemPermission('quote-templates')
    },
  ];

  useEffect(() => {
    primaryNavigationList.forEach(({ id, href, children, matchDynamicRoute, excludedSubroutes }) => {
      if (!id) return;
  
      const isChildActive = children?.some(({ href: childHref }) => location.pathname.startsWith(childHref));
  
      const isDynamicMatch = matchDynamicRoute 
        ? isDynamicRouteMatch(location.pathname, href, excludedSubroutes)
        : false;
  
      if (isChildActive || isDynamicMatch) {
        setPanelState((prev) => ({ ...prev, [id]: true }));
      }
    });
  }, [location.pathname, primaryNavigationList]);

  const appVersion = getAppVersion();

  const companyName = user?.companyData?.name || '';

  return (
    <>
      <Sidebar className="relative">
        {/* Floating minimize button */}
        <button
          onClick={toggleMinimized}
          className={clsx(
            "hidden lg:flex items-center justify-center",
            "absolute top-9 right-0 z-50",
            "h-8 w-8 rounded-full",
            "bg-white dark:bg-zinc-800",
            "border border-zinc-200 dark:border-zinc-700",
            "shadow-md hover:shadow-lg",
            "transition-all duration-200",
            "hover:bg-zinc-50 dark:hover:bg-zinc-700",
            "translate-x-1/2",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900"
          )}
          aria-label={isMinimized ? 'Expand sidebar' : 'Minimize sidebar'}
          title={isMinimized ? t('SIDEBAR_MINIMIZE') : t('SIDEBAR_MINIMIZE')}
        >
          {isMinimized ? (
            <ChevronRightIcon className="h-4 w-4 text-zinc-600 dark:text-zinc-300" />
          ) : (
            <ChevronLeft className="h-4 w-4 text-zinc-600 dark:text-zinc-300" />
          )}
        </button>

        <SidebarHeader className='!pt-0'>
          <SidebarSection>
            <div className={clsx(
              "hidden lg:flex items-center gap-2 w-full py-4 border-b transition-all duration-300",
              isMinimized ? "justify-center pl-0" : "justify-center pl-2"
            )}>
              {isMinimized ? (
                <img src='/logo/nimax-logo-only.svg' alt='nimax logo' className="h-5 w-auto" />
              ) : (
                <img src='/logo/nimax-logo.svg' alt='nimax logo' className="h-5 w-auto" />
              )}
            </div>
          </SidebarSection>
        </SidebarHeader>

        <SidebarBody>
          <SidebarSection>
            {primaryNavigationList.map(({ id, name, href, icon, enabled, matchDynamicRoute, excludedSubroutes, children }) => {
              // Get permission from centralized config using item id
              const hasAccess = hasNavPermission(id);

              // Only render if enabled and user has access
              if (!enabled || !hasAccess) {
                return null;
              }

              return (
                <Fragment key={`${name}-${href}`}>
                  {/* Enabled item - with navigation */}
                  <NavLink to={href} onClick={() => id && togglePanelState(id, true)}>
                    <SidebarItem
                      current={
                        location.pathname === href ||
                        (matchDynamicRoute && isDynamicRouteMatch(location.pathname, href, excludedSubroutes))
                      }
                      className={clsx(
                        'cursor-pointer',
                        panelState[id || ''] && 'font-semibold',
                        isMinimized && 'justify-center'
                      )}
                      title={isMinimized ? name : undefined}
                    >
                      {icon}
                      {!isMinimized && <SidebarLabel>{name}</SidebarLabel>}

                      {children && id && !isMinimized && (
                        <motion.span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            togglePanelState(id);
                          }}
                          className="ml-auto cursor-pointer hover:bg-blue-500/10 rounded-md"
                          animate={{ rotate: panelState[id] ? 90 : 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                          <ChevronRight size={16} />
                        </motion.span>
                      )}
                    </SidebarItem>
                  </NavLink>

                  {children && !isMinimized && (
                    <Fragment>
                      {(() => {
                        const AnimatePresenceComponent = AnimatePresence as any;
                        return (
                          <AnimatePresenceComponent initial={false}>
                            <motion.div
                              key={`${id}-submenu`}
                              variants={submenuVariants}
                              initial="hidden"
                              animate={panelState[id || ''] ? 'visible' : 'hidden'}
                              exit="exit"
                              className="overflow-hidden flex flex-col gap-0.5"
                            >
                              {children.map(({ name: childName, href: childHref, icon: childIcon }) => (
                                  <motion.div key={`${childName}-${childHref}`} variants={submenuItemVariants}>
                                    <NavLink to={childHref}>
                                      <SidebarItem
                                        current={location.pathname === childHref}
                                        className="block h-[36px]"
                                      >
                                        {childIcon}
                                        <SidebarLabel>{childName}</SidebarLabel>
                                      </SidebarItem>
                                    </NavLink>
                                  </motion.div>
                                ))}
                            </motion.div>
                          </AnimatePresenceComponent>
                        );
                      })()}
                    </Fragment>
                  )}
                </Fragment>
              );
            })}
          </SidebarSection>

          <SidebarSection>
            {secondaryNavigationList.map(({ id, name, href, icon, enabled, children }) => {
              // Check access using centralized config - supports both adminOnly and permissions
              const itemAdminOnly = isItemAdminOnly(id);
              const hasAccess = itemAdminOnly
                ? canAccessAdminOnly
                : hasNavPermission(id);

              // Only render if enabled and user has access
              if (!enabled || !hasAccess) {
                return null;
              }

              return (
                <Fragment key={`${name}-${href}`}>
                  {/* Enabled item - with navigation */}
                  <NavLink to={href}>
                    <SidebarItem
                      current={location.pathname === href}
                      className={clsx(
                        'cursor-pointer',
                        isMinimized && 'justify-center'
                      )}
                      title={isMinimized ? name : undefined}
                    >
                      {icon}
                      {!isMinimized && <SidebarLabel>{name}</SidebarLabel>}

                      {name === t('SIDEBAR_NOTIFICATIONS') && unreadCount > 0 && (
                        <Badge color={unreadCount > 0 ? 'blue' : 'zinc'} className='h-5 w-5'>
                          {unreadCount}
                        </Badge>
                      )}
                    </SidebarItem>
                  </NavLink>

                  {children && panelState[id || ''] && (
                    <>
                      {children.map(({ name, href, icon }) => (
                        <NavLink to={href} key={`${name}-${href}`}>
                          <SidebarItem
                            current={location.pathname === href}
                          >
                            {icon}
                            <SidebarLabel>{name}</SidebarLabel>
                          </SidebarItem>
                        </NavLink>
                      ))}
                    </>
                  )}
                </Fragment>
              );
            })}
            
            <SidebarItem
              current={showNotificationsModal}
              className={clsx(
                featureConfig.notifications ? 'cursor-pointer' : '!cursor-not-allowed',
                isMinimized && 'justify-center'
              )}
              disabled={!featureConfig.notifications}
              onClick={handleOpenNotificationsModal}
              title={isMinimized ? t('SIDEBAR_NOTIFICATIONS') : undefined}
            >
              <div className="relative">
                <Bell size={16} />
                {unreadCount > 0 && isMinimized && (
                  <span className="absolute -top-1.5 -right-2.5 h-4 min-w-[16px] text-[10px] font-bold bg-blue-500 text-white rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </div>
              {!isMinimized && <SidebarLabel>{t('SIDEBAR_NOTIFICATIONS')}</SidebarLabel>}

              {unreadCount > 0 && !isMinimized && (
                <Badge color={unreadCount > 0 ? 'blue' : 'zinc'} className='h-5'>
                  {unreadCount}
                </Badge>
              )}
            </SidebarItem>
          </SidebarSection>

          <SidebarSpacer />

          {/* Admin Panel - Only visible to Superadmins */}
          {featureConfig.companies && isSuperAdmin && (
            <SidebarSection>
              <SidebarItem
                href="/admin"
                current={location.pathname.startsWith('/admin')}
                className={clsx(
                  isMinimized && 'justify-center'
                )}
                title={isMinimized ? 'Admin' : undefined}
              >
                <ShieldCheck size={16} />
                {!isMinimized && <SidebarLabel>Admin</SidebarLabel>}
              </SidebarItem>
            </SidebarSection>
          )}

          <SidebarSection>
            <SidebarItem 
              onClick={toggleDarkMode} 
              className={clsx(
                'cursor-pointer',
                isMinimized && 'justify-center'
              )}
              title={isMinimized ? t('SIDEBAR_THEME') : undefined}
            >
              {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
              {!isMinimized && <SidebarLabel>{t('SIDEBAR_THEME')}</SidebarLabel>}
            </SidebarItem>

            <LanguageSelector isMinimized={isMinimized} />
          </SidebarSection>

          {!isMinimized && (
            <>
              <Text className='px-3 pt-2'>
                {t('SIDEBAR_VERSION')}
                <Strong className='pl-1'>{appVersion}</Strong>
              </Text>

              {!isSuperAdmin && (
                <>
                  <SidebarDivider />    

                  <div className='px-3 flex gap-3 items-center'>
                    <Building2 size={16} className="stroke-zinc-500 dark:stroke-zinc-500" />

                    <Text className='!leading-4'>
                      <Strong className='!leading-4'>
                        {companyName}
                      </Strong>
                    </Text>
                  </div>
                </>
              )}
            </>
          )}
        </SidebarBody>

        <SidebarFooter className="max-lg:hidden">
          <UserMenu type='sidebar' isMinimized={isMinimized} extraItems={userMenuExtraItems} />
        </SidebarFooter>
      </Sidebar>
    </>
  );
};

export default SidebarMain;
